// src/components/Experiment/AnalysisToolsContainer.js
import React from 'react';
import { Button, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './AnalysisToolsContainer.css';

const AnalysisToolsContainer = ({ onAnalysisSelect, onExportExcel, dataInfo }) => {
  const analysisTools = [
    {
      id: 'pca',
      name: 'PCA',
      label: 'PCA',
      description: 'Principal Component Analysis',
      color: 'primary',
      category: 'Dimensionality Reduction'
    },
    {
      id: 'lda',
      name: 'LDA',
      label: 'LDA',
      description: 'Linear Discriminant Analysis',
      color: 'success',
      category: 'Classification'
    },
    {
      id: 'logistic',
      name: 'Logistic',
      label: 'LOG',
      description: 'Logistic Regression',
      color: 'warning',
      category: 'Classification'
    },
    {
      id: 'svm',
      name: 'SVM',
      label: 'SVM',
      description: 'Support Vector Machine',
      color: 'danger',
      category: 'Classification'
    },
    {
      id: 'random_forest',
      name: 'Random Forest',
      label: 'RF',
      description: 'Random Forest Classifier',
      color: 'success',
      category: 'Classification'
    },
    {
      id: 'knn',
      name: 'KNN',
      label: 'KNN',
      description: 'K-Nearest Neighbors',
      color: 'secondary',
      category: 'Classification'
    },
    {
      id: 'plsda',
      name: 'PLS-DA',
      label: 'PLS',
      description: 'Partial Least Squares - Discriminant Analysis',
      color: 'info',
      category: 'Classification'
    },
    {
      id: 'simca',
      name: 'SIMCA',
      label: 'SIM',
      description: 'Soft Independent Modeling of Class Analogies',
      color: 'warning',
      category: 'Classification'
    },
    // Add Pipeline tool
    {
      id: 'pipeline',
      name: 'Pipeline',
      label: 'PIPE',
      description: 'Pipeline model with PLS head and classifier (supports GridSearch)',
      color: 'primary',
      category: 'Advanced Classification'
    },
    // Add XGBoost tool
    {
      id: 'xgboost',
      name: 'XGBoost',
      label: 'XGB',
      description: 'Extreme Gradient Boosting - High-performance gradient boosting',
      color: 'info',
      category: 'Advanced Classification'
    },
    // Add Balanced Bagging Classifier tool
    {
      id: 'bbc',
      name: 'Bagging',
      label: 'BBC',
      description: 'Balanced Bagging Classifier for imbalanced datasets',
      color: 'success',
      category: 'Imbalanced Learning'
    },
    // Add Balanced Random Forest tool
    {
      id: 'brf',
      name: 'Balanced RF',
      label: 'BRF',
      description: 'Balanced Random Forest for imbalanced datasets',
      color: 'danger',
      category: 'Imbalanced Learning'
    }
  ];

  // New adjustments tools
  const adjustmentsTools = [
    {
      id: 'create_adjustments',
      name: 'Create Adjustments',
      label: 'ADJ',
      description: 'Create or edit plate adjustment coefficients',
      color: 'dark',
      icon: 'bi-sliders',
      action: () => onAnalysisSelect('create_adjustments')
    },
  ];

  // All analyses button tool (updated to include all methods)
  const allAnalysesTool = {
    id: 'all',
    name: 'All Analyses',
    label: 'ALL',
    description: 'Run all analysis methods in batch (8 methods)',
    color: 'secondary',
    category: 'Batch Processing'
  };

  // Group tools by category for better organization
  const categorizedTools = analysisTools.reduce((acc, tool) => {
    if (!acc[tool.category]) {
      acc[tool.category] = [];
    }
    acc[tool.category].push(tool);
    return acc;
  }, {});

  const renderTooltip = (text) => (
    <Tooltip id="button-tooltip">
      {text}
    </Tooltip>
  );

  // Function to chunk array into groups of 4
  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  const chunkedAllTools = chunkArray(analysisTools, 4);
  const chunkedAdjustmentsTools = chunkArray(adjustmentsTools, 4);

  return (
    <div className="analysis-tools-container">
      {/* Data Info */}
      <div className="tools-section">
        <div className="tools-header">
          <small>Dataset Overview</small>
        </div>
        <div className="tools-content">
          <div className="tools-grid-3 mb-2">
            <Badge bg="primary" className="dataset-badge">{dataInfo.rows} cells</Badge>
            <Badge bg="secondary" className="dataset-badge">{dataInfo.columns} channels</Badge>
            <Badge bg="success" className="dataset-badge">{dataInfo.samples} samples</Badge>
          </div>
        </div>
      </div>

      {/* Divider Line */}
      <div className="tools-divider"></div>

      {/* Analysis Tools - Single Compact Section */}
      <div className="tools-section">
        <div className="tools-header">
          <small>Analysis Tools</small>
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip(allAnalysesTool.description)}
          >
            <Button
              variant="outline-secondary"
              size="sm"
              className="text-button tool-button all-analysis-button"
              onClick={() => onAnalysisSelect(allAnalysesTool.id)}
              data-label={allAnalysesTool.label}
            >
              {allAnalysesTool.label}
            </Button>
          </OverlayTrigger>
        </div>
        <div className="tools-content">
          {/* Row 1: Core Dimensionality & Basic Classification */}
          <div className="tools-row">
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Principal Component Analysis")}
            >
              <Button
                variant="outline-primary"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('pca')}
                data-label="PCA"
              >
                PCA
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Linear Discriminant Analysis")}
            >
              <Button
                variant="outline-success"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('lda')}
                data-label="LDA"
              >
                LDA
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Partial Least Squares - Discriminant Analysis")}
            >
              <Button
                variant="outline-info"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('plsda')}
                data-label="PLS"
              >
                PLS
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Soft Independent Modeling of Class Analogies")}
            >
              <Button
                variant="outline-warning"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('simca')}
                data-label="SIM"
              >
                SIM
              </Button>
            </OverlayTrigger>
          </div>

          {/* Row 2: Traditional Classifiers */}
          <div className="tools-row">
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Logistic Regression")}
            >
              <Button
                variant="outline-warning"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('logistic')}
                data-label="LOG"
              >
                LOG
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Support Vector Machine")}
            >
              <Button
                variant="outline-danger"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('svm')}
                data-label="SVM"
              >
                SVM
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Random Forest Classifier")}
            >
              <Button
                variant="outline-success"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('random_forest')}
                data-label="RF"
              >
                RF
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("K-Nearest Neighbors")}
            >
              <Button
                variant="outline-secondary"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('knn')}
                data-label="KNN"
              >
                KNN
              </Button>
            </OverlayTrigger>
          </div>

          {/* Row 3: Advanced & Imbalanced Learning */}
          <div className="tools-row">
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Pipeline model with PLS head and classifier (supports GridSearch)")}
            >
              <Button
                variant="outline-primary"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('pipeline')}
                data-label="PIPE"
              >
                PIPE
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Extreme Gradient Boosting - High-performance gradient boosting")}
            >
              <Button
                variant="outline-info"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('xgboost')}
                data-label="XGB"
              >
                XGB
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Balanced Bagging Classifier for imbalanced datasets")}
            >
              <Button
                variant="outline-success"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('bbc')}
                data-label="BBC"
              >
                BBC
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Balanced Random Forest for imbalanced datasets")}
            >
              <Button
                variant="outline-danger"
                size="sm"
                className="tool-button text-button"
                onClick={() => onAnalysisSelect('brf')}
                data-label="BRF"
              >
                BRF
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      {/* New Adjustments Tools */}
      <div className="tools-divider"></div>
      <div className="tools-section">
        <div className="tools-header">
          <small>Data Adjustments</small>
        </div>
        <div className="tools-content">
          {chunkedAdjustmentsTools.map((row, rowIndex) => (
            <div key={rowIndex} className="tools-row">
              {row.map(tool => (
                <OverlayTrigger
                  key={tool.id}
                  placement="top"
                  overlay={renderTooltip(tool.description)}
                >
                  <Button
                    variant={`outline-${tool.color}`}
                    size="sm"
                    className="tool-button text-button"
                    onClick={tool.action}
                    data-label={tool.label}
                  >
                    {tool.label}
                  </Button>
                </OverlayTrigger>
              ))}
              {row.length < 4 && Array.from({ length: 4 - row.length }).map((_, index) => (
                <div key={`empty-${index}`} className="tool-button-empty"></div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalysisToolsContainer;